import * as Sentry from '@sentry/react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
   children: ReactNode;
   /**
    * Names the region in Sentry and decides how loudly it fails. `page` takes
    * over the viewport; `section` degrades one part of a page and leaves the
    * rest usable.
    */
   name: string;
   variant?: 'page' | 'section';
};

/**
 * The app had no error boundary at all, so any render error unmounted the whole
 * tree and left a blank white page — Sentry received the exception, the traveler
 * received nothing. Observed for real: a rate without grouped `options` threw
 * inside SelectRate and blanked the season planner.
 *
 * Wrapping a region does three things: the rest of the page survives, the
 * traveler gets a way out, and the Sentry event carries the region name and the
 * React component stack instead of arriving as a bare global error.
 */
const Fallback = ({ name, variant, resetError }: { name: string; variant: 'page' | 'section'; resetError: () => void }) => {
   const [t] = useTranslation();

   return (
      <div
         role="alert"
         data-error-boundary={name}
         className={`flex flex-col items-center justify-center gap-3 rounded-10 border border-border bg-white text-center ${
            variant === 'page' ? 'mx-auto my-12 max-w-[560px] p-7.5' : 'p-5'
         }`}
      >
         <p className="text-lg font-bold text-black">{t('errorBoundary.title')}</p>
         <p className="text-sm text-twGray">{t('errorBoundary.body')}</p>
         <div className="flex flex-wrap items-center justify-center gap-2">
            <button type="button" onClick={resetError} className="h-9 rounded-md bg-orgCallToAction px-3.75 text-sm font-bold text-white">
               {t('errorBoundary.retry')}
            </button>
            <button
               type="button"
               onClick={() => window.location.reload()}
               className="h-9 rounded-10 border border-border px-3.75 text-sm font-semibold text-twGray"
            >
               {t('errorBoundary.reload')}
            </button>
         </div>
      </div>
   );
};

const ErrorBoundary = ({ children, name, variant = 'section' }: Props) => (
   <Sentry.ErrorBoundary
      beforeCapture={scope => {
         scope.setTag('errorBoundary', name);
         scope.setContext('errorBoundary', { name, variant });
      }}
      fallback={({ resetError }) => <Fallback name={name} variant={variant} resetError={resetError} />}
   >
      {children}
   </Sentry.ErrorBoundary>
);

export default ErrorBoundary;
